import { FC } from 'react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import { usePrompt } from '@yakoffice/shared-components';
import Button from 'react-bootstrap/Button';
import { StringInput } from '../common/StringInput';
import { GameVersionVersionInformation } from './GameVersionVersionInformation';
import { IGameVersion } from '../../../model/gameVersion/GameVersion';
import { useGameVersionController } from '../../../controllers/useGameVersionController';
import DropdownButton from 'react-bootstrap/DropdownButton';
import { GameVersionStatus } from '@yakoffice/publisher-types';
import Dropdown from 'react-bootstrap/Dropdown';
import { statusColorClass } from './gameVersionStatusColorClass';


export interface GameVersionCardProps {
    gameVersion: IGameVersion;
    updateCallbackTrigger: (clearActiveGameVersion?: boolean) => void;
}

export const GameVersionForm: FC<GameVersionCardProps> = observer((props: GameVersionCardProps) => {

    const [pageEdited, setPageEdited] = usePrompt('You have unsaved changes. Are you sure you want to leave?', false);

    // Flush sync to prevent batching which prevents immediate rerender
    const controller = useGameVersionController({
        setGameVersionEditStatus: setPageEdited,
        gameVersion: props.gameVersion,
        updateCallbackTrigger: props.updateCallbackTrigger
    });

    const handleNameChange = (value: string) => {
        setPageEdited(true);
        props.gameVersion.setName(value);
    };

    const handleDescriptionChange = (value: string) => {
        setPageEdited(true);
        props.gameVersion.setDescription(value);
    };

    const handleUrlChange = (value: string) => {
        setPageEdited(true);
        props.gameVersion.setConfigApiPublishingUrlVersionId(value);
    };

    return (
        <div className="m3CardForm">
            <Row>
                <Col xl="6">
                    <Form.Group className="position-relative">
                        <Form.Label className="">Name</Form.Label>
                        <StringInput id="geName" value={props.gameVersion.name} handleOnChange={handleNameChange}
                                     disabled={props.gameVersion.name === 'staticData'}
                                     data-testid="txtgameVersionName" />
                    </Form.Group>
                </Col>
                <Col xl="6">
                    <Form.Group className="position-relative">
                        <Form.Label>Description</Form.Label>
                        <StringInput id="geDescription" value={props.gameVersion.description}
                                     handleOnChange={handleDescriptionChange} data-testid="txtgameVersionDescription" />
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mt-4">
                <Col md="6">
                    <Form.Group className="position-relative">
                        <Form.Label>
                            Config Api Publishing Url <GameVersionVersionInformation />
                        </Form.Label>
                        <StringInput
                            id="geUrl"
                            value={props.gameVersion.configApiPublishingUrlVersionId}
                            disabled={props.gameVersion.name === 'staticData'}
                            handleOnChange={handleUrlChange}
                            data-testid="txtgameVersionPubUrl"
                        />
                    </Form.Group>
                </Col>
            </Row>

            <div className="footer">
                {pageEdited && (
                    <Button onClick={() => controller.handleSaveGameVersion(props.updateCallbackTrigger)}
                            disabled={!pageEdited} className="saveButton" data-testid="btnSave">
                        <i className="fas fa-save" />
                        Save
                    </Button>
                )}

                {!props.gameVersion.isNewGameVersion() && (
                    <DropdownButton
                        disabled={props.gameVersion.name === 'staticData'}
                        id="statusChange"
                        title={
                            <span className="text-capitalize">
                                <i className={`fas fa-circle ${statusColorClass(props.gameVersion.status)}`} />
                                {props.gameVersion.status}
                            </span>
                        }
                        data-testid="btnStatusDropdownGameVersion"
                    >
                        {Object.values(GameVersionStatus)
                            .filter((s) => s !== props.gameVersion.status)
                            .map((s) => (
                                <Dropdown.Item
                                    key={s}
                                    onClick={() => controller.handleStatusChange(s, props.updateCallbackTrigger)}
                                    className="text-capitalize"
                                    data-testid={`btnStatus${s}GameVersion`}
                                >
                                    {s}
                                </Dropdown.Item>
                            ))}
                    </DropdownButton>
                )}

                {!props.gameVersion.isNewGameVersion() && (
                    <Button
                        data-value="branch"
                        disabled={props.gameVersion.name === 'staticData'}
                        onClick={() => controller.handleBranchGameVersion(props.updateCallbackTrigger)}
                        data-testid="btnBranchGameVersion"
                    >
                        <i className="fas fa-code-branch" />
                        Branch
                    </Button>
                )}

                {!props.gameVersion.isNew && (
                    <Button
                        variant="danger"
                        className="deleteButton"
                        disabled={props.gameVersion.name === 'staticData'}
                        onClick={() => controller.handleDeleteGameVersion(props.updateCallbackTrigger)}
                    >
                        <i className="fas fa-trash" />
                        Delete
                    </Button>
                )}
            </div>
        </div>
    );
});
